// CookieModal.js

const { delegate } = require("./utils/dom");

class CookieModal {
  constructor(cookieConsent) {
    this.cookieConsent = cookieConsent;
    this.init();
  }

  init() {
    // Cache DOM elements
    this.container = document.querySelector(".mad-cc-modal__container");
    if (!this.container) return;

    this.modal = this.container.querySelector(".mad-cc-modal");
    this.form = this.modal
      ? this.modal.querySelector(".mad-cc-modal__form")
      : null;
    this.closeButton = this.modal
      ? this.modal.querySelector(".mad-cc-modal__button--close")
      : null;
    this.acceptAllButton = this.modal
      ? this.modal.querySelector(".mad-cc-modal__button--accept-all")
      : null;

    this.categoryCheckboxes = this.modal
      ? Array.from(
          this.modal.querySelectorAll(".mad-cc-modal__category-checkbox")
        )
      : [];
    this.cookieCheckboxes = this.modal
      ? Array.from(
          this.modal.querySelectorAll(".mad-cc-modal__cookie-checkbox")
        )
      : [];

    this.bindEvents();
  }

  bindEvents() {
    if (!this.modal) return;

    // Category header click. The category checkbox lives INSIDE the header
    // anchor, so a single guarded listener replaces jQuery's delegated
    // "checkbox stopPropagation + header toggle" pair: clicking the checkbox
    // must not expand/collapse the category (and must not be preventDefault-ed,
    // or the checkbox would stop toggling).
    this.modal.addEventListener("click", (e) => {
      // Clicks on the category checkbox OR its wrapping label must toggle the
      // checkbox natively — never expand/collapse the category, and never be
      // preventDefault-ed (that would cancel the label -> checkbox toggle).
      if (
        e.target.closest(
          ".mad-cc-modal__category-label, .mad-cc-modal__category-checkbox"
        )
      ) {
        return;
      }
      const header = e.target.closest(".mad-cc-modal__category-header");
      if (header) {
        e.preventDefault();
        this.onCategoryToggle(header);
      }
    });

    // Category checkbox change → sync child cookies
    delegate(
      this.modal,
      "change",
      ".mad-cc-modal__category-checkbox",
      (e, checkbox) => this.onCategoryCheckboxChange(checkbox)
    );

    // Cookie checkbox change → sync parent category
    delegate(
      this.modal,
      "change",
      ".mad-cc-modal__cookie-checkbox",
      (e, checkbox) => this.onCookieCheckboxChange(checkbox)
    );

    // Form submission
    if (this.form) {
      this.form.addEventListener("submit", (e) => this.onFormSubmit(e));
    }

    // Close modal button
    if (this.closeButton) {
      this.closeButton.addEventListener("click", () => this.onClose());
    }

    // Accept all button
    if (this.acceptAllButton) {
      this.acceptAllButton.addEventListener("click", () => this.onAcceptAll());
    }
  }

  show() {
    if (!this.container) return;
    this.container.classList.add("active");
    this.updateCheckboxStates();
  }

  hide() {
    if (this.container) this.container.classList.remove("active");
  }

  onAcceptAll() {
    // Delegate consent granting to CookieConsent
    this.cookieConsent.acceptAll();
  }

  updateCheckboxStates() {
    const categoriesInfo = this.cookieConsent.settings.categoriesInfo;

    // Update category checkboxes
    this.categoryCheckboxes.forEach((checkbox) => {
      const categorySlug = checkbox.dataset.category;
      const isNecessary = categoriesInfo[categorySlug]?.is_necessary;

      if (!isNecessary) {
        checkbox.checked =
          this.cookieConsent.getCookieCategoryConsent(categorySlug);
      }
    });

    // Update cookie checkboxes
    this.cookieCheckboxes.forEach((checkbox) => {
      const cookieSlug = checkbox.dataset.cookie;
      const categorySlug = checkbox.dataset.category;
      const isNecessary = categoriesInfo[categorySlug]?.is_necessary;

      if (!isNecessary) {
        checkbox.checked = this.cookieConsent.getCookieConsent(cookieSlug);
      }
    });
  }

  onCategoryToggle(header) {
    const category = header.closest(".mad-cc-modal__category");
    if (!category) return;
    const isActive = category.classList.contains("active");

    // Collapse all, then expand the clicked one (unless it was already open)
    this.modal
      .querySelectorAll(".mad-cc-modal__category")
      .forEach((el) => el.classList.remove("active"));

    if (!isActive) {
      category.classList.add("active");
    }
  }

  onCategoryCheckboxChange(checkbox) {
    const categorySlug = checkbox.dataset.category;
    const isChecked = checkbox.checked;

    // Update all associated (enabled) cookie checkboxes
    this.cookieCheckboxes
      .filter((cb) => cb.dataset.category === categorySlug && !cb.disabled)
      .forEach((cb) => {
        cb.checked = isChecked;
      });
  }

  onCookieCheckboxChange(checkbox) {
    const categorySlug = checkbox.dataset.category;
    const categoryCheckbox = this.categoryCheckboxes.find(
      (cb) => cb.dataset.category === categorySlug
    );

    // Reflect "all cookies checked" onto the category checkbox
    const allChecked = this.cookieCheckboxes
      .filter((cb) => cb.dataset.category === categorySlug)
      .every((cb) => cb.checked || cb.disabled);

    if (categoryCheckbox && !categoryCheckbox.disabled) {
      categoryCheckbox.checked = allChecked;
    }
  }

  onFormSubmit(event) {
    event.preventDefault();

    const cookieConsent = this.cookieConsent;
    const categoriesInfo = cookieConsent.settings.categoriesInfo;

    // Only touch what actually changed, so an unchanged save fires no scripts.
    this.cookieCheckboxes.forEach((checkbox) => {
      const cookieName = checkbox.dataset.cookie;
      const categorySlug = checkbox.dataset.category;
      const isNecessary = categoriesInfo[categorySlug]?.is_necessary;
      const wanted = checkbox.checked || isNecessary;
      const current = cookieConsent.getCookieConsent(cookieName);

      if (wanted && !current) {
        cookieConsent.giveCookieConsent(cookieName);
      } else if (!wanted && current) {
        cookieConsent.revokeCookieConsent(cookieName);
      }
    });

    this.categoryCheckboxes.forEach((checkbox) => {
      const categorySlug = checkbox.dataset.category;
      const isNecessary = categoriesInfo[categorySlug]?.is_necessary;
      const wanted = checkbox.checked || isNecessary;
      const current = cookieConsent.getCookieCategoryConsent(categorySlug);

      if (wanted && !current) {
        cookieConsent.giveCookieCategoryConsent(categorySlug);
      } else if (!wanted && current) {
        // Category-level only — the loop above handled the cookies, some may stay granted.
        cookieConsent.revokeCategoryLevelConsent(categorySlug);
      }
    });

    // Set cookie to hide the banner
    this.cookieConsent.setCookie("hide_cookie_banner", "true", 365);

    // Hide modal and banner
    this.hide();
    this.cookieConsent.banner.hide();
    this.cookieConsent.trigger.syncVisibility();

    // Apply consent changes
    this.cookieConsent.applyConsent();

    // Log consent
    this.cookieConsent.logConsent();
  }

  onClose() {
    this.hide();
  }
}

module.exports = CookieModal;
